import { ActionIcon, Text, rem } from '@mantine/core';
import { memo } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { OverflowBadgeList } from '@dealstack/frontend/app/pages/Valuations/components/OverflowBadgeList/OverflowBadgeList';
import {
	ClassifierOption,
	getEntityClassifierBadgeConfig,
} from '@dealstack/frontend/app/pages/Valuations/shared/entityClassiferBadgeConfig';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ROW_HEIGHT } from '@dealstack/frontend/app/pages/Valuations/components/table/constants';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';

const INDENTATION_PER_LEVEL_PX = 16;

interface Props extends Record<string, unknown> {
	value: string | number | null;
	row: {
		original: {
			metadata?: {
				classifiers?: ClassifierOption[];
				type?: string;
				depth?: number;
				isTotal?: boolean;
				isSummingRow?: boolean;
			};
			subRows?: unknown[];
		};
		getIsExpanded: () => boolean;
		toggleExpanded: () => void;
	};
	isExpanded?: boolean;
	onExpansionToggle?: () => void;
	maxHeight?: number;
	isLastRow?: boolean;
	isLastColumn?: boolean;
	/** Column index for E2E testing - used as data-testindex attribute */
	columnIndex?: number;
}

export const NewEntityCell = memo<Props>(
	({
		value,
		row,
		isExpanded,
		onExpansionToggle,
		maxHeight,
		isLastRow,
		isLastColumn,
		columnIndex,
	}) => {
		const tCapManage = useTranslationCapManage();

		const handleExpand = (e: React.MouseEvent) => {
			e.stopPropagation();
			row.toggleExpanded();
			onExpansionToggle?.();
		};

		const hasSubRows = !!row.original.subRows?.length;
		const isRowExpanded = isExpanded ?? row.getIsExpanded();
		const isNonSummingTotalRow =
			row.original.metadata?.isTotal && !row.original.metadata?.isSummingRow;

		const backgroundColor = isNonSummingTotalRow
			? defaultTheme.v3.colors.gray[0]
			: defaultTheme.v3.colors.white[0];
		const borderRight =
			isNonSummingTotalRow || isLastColumn
				? 'none'
				: `1px solid ${defaultTheme.v3.colors.gray[4]}`;
		const borderBottom = isLastRow ? 'none' : `1px solid ${defaultTheme.v3.colors.gray[4]}`;

		const rowDepth = row.original.metadata?.depth ?? 0;
		const indentationPx = rowDepth * INDENTATION_PER_LEVEL_PX;

		const containerStyle: React.CSSProperties = {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			flexDirection: 'row',
			flex: 1,
			minWidth: 0,
			width: '100%',
			paddingLeft: rem(16 + indentationPx), // Base 16px + additional indentation
			gap: '0.5rem',
			backgroundColor,
			borderRight,
			borderBottom,
			maxHeight: maxHeight ?? `${ROW_HEIGHT}px`,
		};

		const contentStyle: React.CSSProperties = {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			flex: 1,
			minWidth: 0,
			height: '100%',
			width: '100%',
		};

		const badgesStyle: React.CSSProperties = {
			display: 'flex',
			height: '1.5rem',
			alignItems: 'center',
		};

		const valueString = typeof value === 'number' ? value.toString() : value;
		return (
			<div
				data-testid={makeComplexDataTestId(TEST_ELEMENT_IDS.NEW_ENTITY_CELL, valueString)}
				data-testindex={columnIndex}
				style={containerStyle}
			>
				<div style={contentStyle}>
					<Text
						size="B6Medium"
						c="dark.9"
						lineClamp={1}
						truncate
						fw={isNonSummingTotalRow ? 600 : undefined}
					>
						{value ?? ''}
					</Text>
					{!!row.original.metadata?.classifiers?.length && (
						<div style={badgesStyle}>
							<OverflowBadgeList
								badges={
									row.original.metadata?.classifiers?.map(
										(classifier: ClassifierOption) => {
											const config = getEntityClassifierBadgeConfig(
												classifier.label,
												tCapManage
											);
											return {
												id: config.label,
												label: config.label,
												iconVariant: config.iconVariant,
											};
										}
									) ?? []
								}
							/>
						</div>
					)}
				</div>
				{hasSubRows && (
					<ActionIcon
						data-testid={TEST_ELEMENT_IDS.NEW_ENTITY_CELL_TOGGLE}
						size="lg"
						variant="subtle"
						style={{ backgroundColor: 'transparent' }}
						onClick={handleExpand}
					>
						<Icon
							variant={isRowExpanded ? 'caret-down' : 'caret-right'}
							size="lg"
							color={defaultTheme.v3.colors.black[0]}
						/>
					</ActionIcon>
				)}
			</div>
		);
	}
);
